import { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { getAuthToken } from '../utils/authToken';

const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isLoggedIn } = useUser();

  const checkOnboardingStatus = async () => {
    try {
      setLoading(true);
      
      if (!isLoggedIn || !user) {
        setLoading(false);
        return;
      }
      
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }

      const response = await fetch('/api/onboarding/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setOnboardingStatus(data.data);
        setShowOnboarding(data.data.shouldShowOnboarding);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissOnboarding = async () => {
    try {
      if (!isLoggedIn || !user) {
        return;
      }
      
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch('/api/onboarding/dismiss', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setShowOnboarding(false);
        setOnboardingStatus(prev => ({
          ...prev,
          shouldShowOnboarding: false
        }));
      }
    } catch (error) {
      console.error('Error dismissing onboarding:', error);
    }
  };

  const completeOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingStatus(prev => ({
      ...prev,
      onboarding_completed: true,
      shouldShowOnboarding: false
    }));
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      // Add a small delay to ensure user state is fully loaded after email verification
      const timer = setTimeout(() => {
        checkOnboardingStatus();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      // If user is not logged in, set loading to false immediately
      setLoading(false);
    }
  }, [isLoggedIn, user]);

  return {
    showOnboarding,
    onboardingStatus,
    loading,
    checkOnboardingStatus,
    dismissOnboarding,
    completeOnboarding
  };
};

export default useOnboarding;
